import { createLieArt } from "../../../shared/cards/lieArt";
import { lieArtDefId, lieArtFallbackId, lieById } from "../data/lies";
import type { LayerId } from "../game/board/types";
import { LAYER_LABELS } from "../game/board/types";
import {
  dogmaDemand,
  dogmaIsCore,
  dogmaRemaining,
  isDogmaPresence,
} from "../game/combat/dogma";
import {
  doubtCoreExposed,
  doubtFaithProgress,
} from "../game/combat/doubt";
import { isLieRedeemable } from "../game/combat/redeemable";
import type { Presence, VerbKit } from "../game/cards/types";
import { levelRoman } from "../game/cards/types";

function verbPills(verbs: VerbKit): { label: string; tip: string }[] {
  const out: { label: string; tip: string }[] = [];
  if (verbs.pressure !== undefined) {
    out.push({
      label: `Pressure ${verbs.pressure}`,
      tip: `Darkens this layer over time (~${verbs.pressure}/s) while present.`,
    });
  }
  if (verbs.contest) {
    out.push({
      label: verbs.contestNextLayer
        ? `Contest ahead ${verbs.contest}`
        : `Contest ${verbs.contest}`,
      tip: verbs.contestNextLayer
        ? `Wears an adjacent foe on the next layer toward its advance (rate ${verbs.contest}) — same column or one over.`
        : `Wears an adjacent foe here (rate ${verbs.contest}) — same column or one over. The far wing is out of reach.`,
    });
  }
  if (verbs.guard) {
    out.push({
      label: "Guard",
      tip: "Sturdier: harder to wear down, and darkness presses inward less hard.",
    });
  }
  if (verbs.siegeFirst) {
    out.push({
      label: "Hunts keeps",
      tip: "Hammers a Prayer on this layer instead of slipping into an empty gap. Mend worn keeps — a prayer blanket will not ignore it.",
    });
  }
  if (verbs.taxAfter) {
    const secs = verbs.taxAfter.toFixed(0);
    const hit = verbs.taxHit ?? 12;
    out.push({
      label: `Accuses ${secs}s`,
      tip: `Winds up a strike on the Secret Place (~${secs}s, −${hit} Integrity). The arrow shows the charge. Any Answer, Chip, Soften, or Still rewinds it — even if the lie survives.`,
    });
  }
  if (verbs.advanceAfter) {
    out.push({
      label: `Advance ${verbs.advanceAfter.toFixed(1)}s`,
      tip: `After ~${verbs.advanceAfter.toFixed(1)}s on a dark enough layer, steps one layer closer to the Secret Place.`,
    });
  }
  if (verbs.spreadAfter) {
    out.push({
      label: `Multiply ${verbs.spreadAfter.toFixed(1)}s`,
      tip: `After ~${verbs.spreadAfter.toFixed(1)}s, may spawn a weaker copy in an empty slot.`,
    });
  }
  return out;
}

export class LieInspect {
  private root = document.getElementById("lie-inspect")!;
  private nameEl = document.getElementById("lie-name")!;
  private metaEl = document.getElementById("lie-meta")!;
  private claimEl = document.getElementById("lie-claim")!;
  private keywordsEl = document.getElementById("lie-keywords")!;
  private statsEl = document.getElementById("lie-stats")!;
  private closeBtn = document.getElementById("btn-lie-close")!;
  private hoverTip = document.getElementById("lie-hover-tip");

  constructor() {
    this.closeBtn.addEventListener("click", () => this.hide());
    this.root.addEventListener("click", (e) => {
      if (e.target === this.root) this.hide();
    });
  }

  isOpen(): boolean {
    return !this.root.classList.contains("hidden");
  }

  show(presence: Presence, layer: LayerId, slot: number): void {
    if (presence.side !== "enemy") return;
    const def = lieById(presence.defId);

    this.nameEl.textContent = presence.name;
    const origin =
      presence.lieType === "confusion" && presence.sourceLieId
        ? ` · from ${presence.sourceLieId}`
        : "";
    let intensityBit = levelRoman(presence.level);
    if (presence.doubt) {
      const { faith, total } = doubtFaithProgress(presence.doubt);
      intensityBit = `${faith}/${total} Faith`;
      if (doubtCoreExposed(presence.doubt)) intensityBit += " · core open";
    }
    if (presence.dogma) {
      const left = dogmaRemaining(presence.dogma);
      const demand = dogmaDemand(presence.dogma);
      const label = demand.charAt(0).toUpperCase() + demand.slice(1);
      intensityBit =
        presence.dogma.named || dogmaIsCore(presence.dogma)
          ? `${left} left · ${label}`
          : `${left} left`;
    }
    this.metaEl.textContent = `${presence.lieType ?? "lie"} · ${intensityBit} · ${LAYER_LABELS[layer]} · column ${slot + 1}${origin}`;
    this.claimEl.textContent = presence.claim || def?.stages[0]?.claim || "…";

    // Large art above claim (modal-card structure: name, meta, claim…)
    const card = this.root.querySelector(".lie-card");
    if (card) {
      card.querySelector(".lie-inspect-art")?.remove();
      const art = createLieArt({
        defId: lieArtDefId(presence),
        fallbackDefId: lieArtFallbackId(presence),
        level: presence.level,
        name: presence.name,
        large: true,
      });
      art.classList.add("lie-inspect-art");
      // Insert after meta, before claim
      this.metaEl.insertAdjacentElement("afterend", art);
    }

    this.keywordsEl.replaceChildren();
    if (isDogmaPresence(presence) && presence.dogma) {
      const demand = dogmaDemand(presence.dogma);
      const label = demand.charAt(0).toUpperCase() + demand.slice(1);
      const peel = document.createElement("span");
      peel.className = "keyword-chip kw-lie";
      peel.textContent = dogmaIsCore(presence.dogma)
        ? `Core · ${label}`
        : presence.dogma.named
          ? `Wants ${label}`
          : "Inspect to Name";
      peel.title =
        "Each peel changes the claim and the category that works. Chip never peels. Don't let it sit on the Will.";
      this.keywordsEl.appendChild(peel);
    }
    if (presence.lieType === "confusion") {
      const chip = document.createElement("span");
      chip.className = "keyword-chip kw-lie";
      chip.textContent = "Weak to all Answers";
      chip.title =
        "Any Answer works well (Faith or Peace). Finish small leftovers before they twist into this.";
      this.keywordsEl.appendChild(chip);
    }
    if (presence.lieType === "doubt" && presence.doubt) {
      const onion = document.createElement("span");
      onion.className = "keyword-chip kw-lie";
      if (presence.doubt.outerCount === 0) {
        onion.textContent = "Core open";
        onion.title =
          "Bare question — Faith Answer or Redeem always turns it into a Prayer (never mere silence). Wrong Word tangles an outer shell and starts the re-doubt timer.";
      } else {
        onion.textContent = "Convince (onion)";
        onion.title =
          "Faith/Wisdom convert outer shells (chips can multi-outer). When the core is open, Faith Answer or Redeem always Redeems into a Prayer. Wrong Answers undo Faith or tangle new shells.";
      }
      this.keywordsEl.appendChild(onion);
      const meets = document.createElement("span");
      meets.className = "keyword-chip kw-lie";
      meets.textContent = "Meets: Faith · Wisdom";
      meets.title =
        "Only Faith or Wisdom turns Doubt toward Faith. Other Words tangle or undo.";
      this.keywordsEl.appendChild(meets);
      if (isLieRedeemable(presence)) {
        const red = document.createElement("span");
        red.className = "keyword-chip kw-lie";
        red.textContent = "Redeemable";
        red.title =
          "Core is open — Faith Answer or Redeem turns this into a Prayer (Doubt is never only silenced).";
        this.keywordsEl.appendChild(red);
      }
    }
    for (const pill of verbPills(presence.verbs)) {
      const chip = document.createElement("span");
      chip.className = "keyword-chip kw-lie";
      chip.textContent = pill.label;
      chip.title = pill.tip;
      this.keywordsEl.appendChild(chip);
    }
    if (presence.slowTimer > 0) {
      const chip = document.createElement("span");
      chip.className = "keyword-chip kw-still";
      chip.textContent = `Still ${presence.slowTimer.toFixed(1)}s`;
      chip.title = "While Still lasts, this lie advances or multiplies much more slowly.";
      this.keywordsEl.appendChild(chip);
    }

    if (presence.doubt) {
      const { faith, total } = doubtFaithProgress(presence.doubt);
      const open = presence.doubt.outerCount - presence.doubt.outerFaith;
      const coreOnly = presence.doubt.outerCount === 0;
      this.statsEl.textContent =
        (coreOnly
          ? "Core only — Answer or Redeem to settle"
          : `Conviction ${faith}/${total}`) +
        (!coreOnly && open > 0
          ? ` · ${open} outer shell${open === 1 ? "" : "s"} still Doubt`
          : !coreOnly
            ? " · core open — Answer or Redeem to settle"
            : "") +
        (isLieRedeemable(presence) ? " · Redeemable" : "") +
        (presence.doubt.decayEnabled
          ? presence.doubt.outerFaith > 0 && open > 0
            ? " · re-doubts if left half-convinced"
            : " · timer on"
          : " · no re-doubt timer yet") +
        (presence.doubt.outerCount < presence.doubt.maxOuterCount
          ? " · wrong Word can tangle further"
          : "") +
        (presence.slowTimer > 0 ? ` · Slowed (decay paused)` : "");
    } else {
      this.statsEl.textContent =
        `Level ${levelRoman(presence.level)} / ${levelRoman(presence.maxLevel)}` +
        (isLieRedeemable(presence)
          ? " · Redeemable (can become a Prayer)"
          : " · Full strength — weaken before Redeem") +
        (presence.wear > 0.05
          ? ` · Wear ${Math.round(presence.wear * 100)}%`
          : "") +
        (presence.advanceProgress > 0.05
          ? ` · Push ${Math.round(presence.advanceProgress * 100)}%`
          : "") +
        ((presence.taxProgress ?? 0) > 0.05 && presence.verbs.taxAfter
          ? ` · Accuses ${Math.round((presence.taxProgress ?? 0) * 100)}%`
          : "") +
        (presence.slowTimer > 0 ? ` · Slowed` : "");
    }

    this.root.classList.remove("hidden");
  }

  hide(): void {
    this.root.classList.add("hidden");
  }

  /** Lightweight hover label near cursor */
  showHover(presence: Presence, clientX: number, clientY: number): void {
    if (!this.hoverTip || presence.side !== "enemy") return;
    const hoverLv = presence.doubt
      ? `${presence.doubt.outerFaith}/${presence.doubt.outerCount + 1} Faith`
      : levelRoman(presence.level);
    this.hoverTip.textContent = `${presence.name} ${hoverLv} — “${presence.claim}” · ${presence.lieType ?? "lie"}`;
    this.hoverTip.classList.remove("hidden");
    const pad = 12;
    let x = clientX + pad;
    let y = clientY + pad;
    const rect = this.hoverTip.getBoundingClientRect();
    if (x + rect.width > window.innerWidth - 8) x = clientX - rect.width - pad;
    if (y + rect.height > window.innerHeight - 8) y = clientY - rect.height - pad;
    this.hoverTip.style.left = `${x}px`;
    this.hoverTip.style.top = `${y}px`;
  }

  hideHover(): void {
    this.hoverTip?.classList.add("hidden");
  }
}
